import React, { useState } from 'react'
import styles from './ContactBox.module.css'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import { FormattedMessage, useIntl } from 'react-intl'
import { Modal } from '~/components/Modal/Modal'
import { LeadErrorNotice } from '~/components/LeadErrorNotice/LeadErrorNotice'
import { submitContactForm } from '~/utils/contactForm'

interface ContactBoxProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  showName?: boolean
  showEmail?: boolean
  showTextarea?: boolean
  modalThankYouMessage?: string
}

const ContactBox: React.FC<ContactBoxProps> = ({
  title,
  subtitle = false,
  showName = false,
  showEmail = false,
  showTextarea = false,
  modalThankYouMessage,
}) => {
  const intl = useIntl()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')

  const [errors, setErrors] = useState<{
    name?: string
    email?: string
    message?: string
  }>({})

  const [modalOpen, setModalOpen] = useState(false)
  const [sendFailed, setSendFailed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (isSubmitting) return

    // validate
    setSendFailed(false)
    const newErrors: { name?: string; email?: string; message?: string } = {}

    if (showName && !name.trim()) {
      newErrors.name = intl.formatMessage({
        id: 'checkout.error.required',
        defaultMessage: 'Acest câmp este obligatoriu',
      })
    }
    if (showEmail && !email.trim()) {
      newErrors.email = intl.formatMessage({
        id: 'checkout.error.required',
        defaultMessage: 'Acest câmp este obligatoriu',
      })
    }
    if (showTextarea && !message.trim()) {
      newErrors.message = intl.formatMessage({
        id: 'checkout.error.required',
        defaultMessage: 'Acest câmp este obligatoriu',
      })
    }

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      return
    }

    // build payload
    const payload = `<b>New Contact</b>
${showName ? `<b>Name:</b> ${name}\n` : ''}${
      showEmail ? `<b>Email:</b> ${email}\n` : ''
    }<b>Message:</b> ${message}`

    setIsSubmitting(true)
    try {
      await submitContactForm({
        text: payload,
        // The backend has no `contact_box` source, and an unknown value is a
        // 400 that drops the lead. This box is a contact form, so it declares
        // itself as one; the `<b>New Contact</b>` header in `text` above still
        // tells the owner which widget it came from.
        source: 'contact_form',
        ...(showName ? { name } : {}),
        ...(showEmail ? { email } : {}),
        message,
      })
    } catch (err) {
      console.error('[contact-box] /api/contact-form failed', err)
      setSendFailed(true)
      setIsSubmitting(false)
      return
    }

    // clear and show thank-you
    setIsSubmitting(false)
    setName('')
    setEmail('')
    setMessage('')
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
  }

  return (
    <div className={styles.boxContainer}>
      <h2 className={styles.boxTitle}>{title}</h2>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

      <div className={styles.form}>
        {showName && (
          <div className={styles.contactForm}>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setErrors((prev) => ({ ...prev, name: '' }))
              }}
              placeholder={intl.formatMessage({
                id: 'contactBox.placeholder.name',
                defaultMessage: 'Your Name',
              })}
              data-testid="contact-box-name"
            />
            {errors.name && <p className={styles.error}>{errors.name}</p>}
          </div>
        )}

        {showEmail && (
          <div className={styles.contactForm}>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setErrors((prev) => ({ ...prev, email: '' }))
              }}
              placeholder={intl.formatMessage({
                id: 'contactBox.placeholder.email',
                defaultMessage: 'Your Email',
              })}
              data-testid="contact-box-email"
            />
            {errors.email && <p className={styles.error}>{errors.email}</p>}
          </div>
        )}

        {showTextarea && (
          <div className={styles.contactForm}>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
                setErrors((prev) => ({ ...prev, message: '' }))
              }}
              placeholder={intl.formatMessage({
                id: 'contactBox.placeholder.message',
                defaultMessage: 'Your Message',
              })}
              data-testid="contact-box-message"
            />
            {errors.message && <p className={styles.error}>{errors.message}</p>}
          </div>
        )}

        <div className={styles.buttonContainer}>
          <CustomButton
            onClick={handleSubmit}
            disabled={isSubmitting}
            data-testid="contact-box-submit"
          >
            <FormattedMessage
              id="homepage.button.sendMessage"
              defaultMessage="Send Message"
            />
          </CustomButton>
        </div>
        {sendFailed && (
          <LeadErrorNotice
            messageId="contactBox.error.sendFailed"
            prefillId="contactBox.whatsapp.prefill"
            className={styles.error}
            testId="contact-box-error"
          />
        )}
      </div>

      <Modal isOpen={modalOpen} onClose={handleCloseModal}>
        <h3 data-testid="contact-box-thankyou">
          <FormattedMessage
            id={modalThankYouMessage}
            defaultMessage="contactForm.modal.thankYouMessage"
          />
        </h3>
      </Modal>
    </div>
  )
}

export default ContactBox
